// Programmer une séance, un match ou un autre événement (staff).
// Récurrence hebdomadaire : jours cochés, jusqu'à une date (fin de saison par
// défaut). Une séance précise s'annule ensuite depuis sa fiche.
import { activeSeason, createEvent, saveMatch, updateEvent, type ClubEvent, type MatchCompetition, type NewEvent } from '../lib/api';
import { isoWeekday, localDate, parseLocalDate } from '../domain/schedule';
import { seasonEnd } from './club';
import type { Ctx } from './context';
import { $, $$, html, openSheet, toast } from './dom';
import { hallFieldHtml, wireHallField } from './salles-form';
import { sallesOn } from './salles-module';
import { openSlotEventEdit, openSlotEventForm } from './seance-creneau';

const DAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const DAY_NAMES = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const DEFAULT_TITLE = { training: 'Entraînement', match: 'Match', other: 'Événement' } as const;
type Kind = keyof typeof DEFAULT_TITLE;

export function openEventForm(ctx: Ctx, onCreated: () => void): void {
  const teams = ctx.teams.filter(t => ctx.staffOf.has(t.id));
  if (!teams.length) return;
  // D-135 : le club a le planning des salles : la séance se pose dans ses créneaux (sans planning général : comme avant).
  const club = ctx.club;
  const here = club ? teams.filter(t => t.club_id === club.id) : [];
  if (sallesOn(ctx) && here.length) { void openSlotEventForm(ctx, here, onCreated, () => openLegacyEventForm(ctx, teams, onCreated)); return; }
  openLegacyEventForm(ctx, teams, onCreated);
}

function openLegacyEventForm(ctx: Ctx, teams: Ctx['teams'], onCreated: () => void): void {
  const today = localDate(new Date());

  openSheet('Programmer', html`
    <form class="form" id="f-ev" novalidate>
      ${teams.length > 1 ? html`
        <label class="field"><span>Équipe</span>
          <select name="team">${teams.map(t => html`<option value="${t.id}">${t.name}</option>`)}</select>
        </label>` : html`<input type="hidden" name="team" value="${teams[0]!.id}">`}
      <div class="seg" role="radiogroup" aria-label="Type">
        ${(Object.keys(DEFAULT_TITLE) as Kind[]).map((k, i) => html`<button type="button" role="radio" data-kind="${k}" aria-pressed="${String(i === 0)}">${DEFAULT_TITLE[k]}</button>`)}
      </div>
      <label class="field title-field"><span>Intitulé</span><input name="title" maxlength="120" placeholder="Entraînement"></label>
      <div class="match-fields form" hidden>
        <label class="field"><span>Adversaire</span><input name="opponent" maxlength="120" placeholder="ex. Valbrenne"></label>
        <div class="seg" role="radiogroup" aria-label="Lieu du match">
          <button type="button" role="radio" data-home="1" aria-pressed="true">À domicile</button>
          <button type="button" role="radio" data-home="0" aria-pressed="false">À l'extérieur</button>
        </div>
        <label class="field"><span>Compétition</span>
          <select name="competition"><option value="championnat">Championnat</option><option value="coupe">Coupe</option><option value="amical">Amical / préparation</option></select>
        </label>
        <label class="field squad-field" hidden><span>Équipe engagée</span><select name="squad"></select></label>
        <p class="hint">Les matchs officiels arrivent tout seuls si l'équipe est reliée à la FFBB (page de l'équipe).</p>
      </div>
      <div class="row-3">
        <label class="field"><span>Date</span><input name="date" type="date" value="${today}" required></label>
        <label class="field"><span>Début</span><input name="start" type="time" value="19:00" required></label>
        <label class="field"><span>Fin</span><input name="end" type="time" value="21:00"></label>
      </div>
      ${hallFieldHtml()}
      <label class="check"><input type="checkbox" name="weekly"><span><strong>Toutes les semaines</strong></span></label>
      <div class="weekly" hidden>
        <div class="week-chips" role="group" aria-label="Jours">
          ${DAYS.map((d, i) => html`<button type="button" data-day="${i + 1}" aria-pressed="false" aria-label="${DAY_NAMES[i]}">${d}</button>`)}
        </div>
        <label class="field"><span>Jusqu'au</span><input name="until" type="date"></label>
      </div>
      <label class="field"><span>Infos pour l'équipe (facultatif)</span><textarea name="note" rows="2" maxlength="500" placeholder="ex. RDV 14h15 pour le covoiturage"></textarea></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Programmer</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-ev')!;
    const msg = $(f, '.form-msg')!;
    const title = $<HTMLInputElement>(f, '[name=title]')!;
    // Un groupe à plusieurs équipes engagées (D-042) : le match est celui de l'une d'elles.
    const squadSel = $<HTMLSelectElement>(f, '[name=squad]')!;
    const fillSquads = () => {
      const teamId = String(new FormData(f).get('team'));
      const list = ctx.squads.filter(q => q.team_id === teamId);
      squadSel.innerHTML = html`<option value="">Tout le groupe</option>${list.map(q => html`<option value="${q.id}">${q.name}</option>`)}`.s;
      $(f, '.squad-field')!.hidden = list.length < 2;
    };
    fillSquads();
    $<HTMLSelectElement>(f, '[name=team]')?.addEventListener('change', fillSquads);
    const date = $<HTMLInputElement>(f, '[name=date]')!;
    const until = $<HTMLInputElement>(f, '[name=until]')!;
    const weekly = $<HTMLInputElement>(f, '[name=weekly]')!;
    let kind: Kind = 'training';

    for (const b of $$<HTMLButtonElement>(f, '[data-kind]')) b.addEventListener('click', () => {
      kind = b.dataset.kind as Kind;
      $$(f, '[data-kind]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      title.placeholder = DEFAULT_TITLE[kind];
      $(f, '.match-fields')!.hidden = kind !== 'match';
      $(f, '.title-field')!.hidden = kind === 'match';     // pour un match, l'intitulé = l'adversaire
      $(f, '[name=weekly]')!.closest('label')!.hidden = kind === 'match';
      if (kind === 'match' && weekly.checked) { weekly.checked = false; weekly.dispatchEvent(new Event('change')); }
    });
    let home = true;
    for (const b of $$<HTMLButtonElement>(f, '[data-home]')) b.addEventListener('click', () => {
      home = b.dataset.home === '1';
      $$(f, '[data-home]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    });
    const days = new Set<number>();
    const paintDays = () => $$(f, '[data-day]').forEach(x => x.setAttribute('aria-pressed', String(days.has(Number(x.dataset.day)))));
    for (const b of $$<HTMLButtonElement>(f, '[data-day]')) b.addEventListener('click', () => {
      const d = Number(b.dataset.day);
      if (days.has(d)) days.delete(d); else days.add(d);
      paintDays();
    });
    weekly.addEventListener('change', async () => {
      $(f, '.weekly')!.hidden = !weekly.checked;
      if (!weekly.checked) return;
      if (!days.size && date.value) { days.add(isoWeekday(parseLocalDate(date.value))); paintDays(); }
      if (!until.value) {
        const team = teams.find(t => t.id === String(new FormData(f).get('team')))!;
        const season = await activeSeason(team.club_id).catch(() => null);
        until.value = season?.ends_on ?? seasonEnd(date.value || today);
      }
    });
    // D-123 : la salle, dans la liste ; ce qui chevauche et ce qui est libre, avant d'enregistrer (jamais bloquant).
    const startIn = $<HTMLInputElement>(f, '[name=start]')!, endIn = $<HTMLInputElement>(f, '[name=end]')!;
    const teamOf = () => String(new FormData(f).get('team'));
    const hall = wireHallField($(f, '[data-hall-field]')!, {
      ctx, clubId: () => teams.find(t => t.id === teamOf())?.club_id ?? null, teamId: teamOf,
      when: () => ({ day: date.value, start: startIn.value, end: endIn.value, weekdays: weekly.checked && days.size ? [...days] : null,
        until: weekly.checked ? until.value || null : null }),
      free: () => kind === 'match' && !home,
      setTime: (d, s, e) => { date.value = d; startIn.value = s; endIn.value = e; },
    });
    f.addEventListener('change', () => hall.refresh());
    f.addEventListener('click', e => { if ((e.target as HTMLElement).closest('[data-kind], [data-home], [data-day]')) hall.refresh(); });

    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const day = String(d.get('date') ?? ''), start = String(d.get('start') ?? ''), end = String(d.get('end') ?? '');
      if (!day || !start) { msg.textContent = 'Indique la date et l\'heure.'; return; }
      const startsAt = new Date(`${day}T${start}`);
      const endsAt = end ? new Date(`${day}T${end}`) : null;
      if (endsAt && endsAt <= startsAt) { msg.textContent = 'La fin doit être après le début.'; return; }
      if (weekly.checked && (!days.size || !until.value || until.value < day)) { msg.textContent = 'Choisis les jours et une date de fin après la première séance.'; return; }
      const opponent = String(d.get('opponent') ?? '').trim();
      if (kind === 'match' && !opponent) { msg.textContent = 'Indique l\'adversaire.'; return; }
      const row: NewEvent = {
        team_id: String(d.get('team')), kind, title: kind === 'match' ? opponent : String(d.get('title') ?? '').trim() || DEFAULT_TITLE[kind],
        starts_at: startsAt.toISOString(), ends_at: endsAt?.toISOString() ?? null,
        ...hall.value(), note: String(d.get('note') ?? '').trim() || null,
        recurrence: weekly.checked ? { weekdays: [...days].sort(), until: until.value } : null,
      };
      try {
        const ev = await createEvent(row);
        if (kind === 'match') {
          await saveMatch({ event_id: ev.id, opponent, is_home: home, competition: String(d.get('competition')) as MatchCompetition,
            squad_id: String(d.get('squad') ?? '') || null });
        }
        close(); toast(weekly.checked ? 'Séances programmées' : 'Programmé'); onCreated();
      }
      catch { msg.textContent = 'Enregistrement impossible. Réessaie.'; }
    });
  });
}

const hhmm = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '');

/**
 * Modifier un événement ; pour une série d'entraînements, toute la série : jours,
 * heures, lieu, date de fin, infos (Idries : « le coach doit pouvoir modifier la série »).
 * Les séances déjà annulées le restent ; les plans et réponses des dates gardées aussi.
 */
export function openEventEdit(ev: ClubEvent, onDone: () => void, ctx?: Ctx): void {
  // D-135 : même règle à l'édition d'une séance (pas d'un match : sa salle vient de la FFBB, ou c'est un amical).
  if (ctx && ev.kind !== 'match' && sallesOn(ctx) && ctx.teams.some(t => t.id === ev.team_id && t.club_id === ctx.club?.id)) {
    void openSlotEventEdit(ev, ctx, onDone, () => openLegacyEventEdit(ev, onDone, ctx));
    return;
  }
  openLegacyEventEdit(ev, onDone, ctx);
}

function openLegacyEventEdit(ev: ClubEvent, onDone: () => void, ctx?: Ctx): void {
  const series = !!ev.recurrence?.weekdays?.length;
  const first = localDate(new Date(ev.starts_at));
  const days = new Set<number>(series ? ev.recurrence!.weekdays : []);
  // D-123 (maquette s3) : une séance a sa salle, choisie dans la liste ; un match garde son lieu (la FFBB le donne).
  const team = ctx?.teams.find(t => t.id === ev.team_id);
  const withHall = !!ctx && !!team && ev.kind !== 'match';
  openSheet(series ? 'Modifier la série' : ev.kind === 'training' ? 'Modifier la séance' : 'Modifier', html`
    <form class="form" id="f-edit" novalidate>
      ${withHall ? html`<label class="field"><span>Équipe</span><select name="team_ro" disabled><option>${team!.name}</option></select></label>
        ${hallFieldHtml(ev.location)}` : ''}
      ${ev.kind !== 'match' && !withHall ? html`<label class="field"><span>Intitulé</span><input name="title" maxlength="120" value="${ev.title}"></label>` : ''}
      ${series ? html`
        <div class="field"><span>Jours</span><div class="week-chips" role="group" aria-label="Jours">
          ${DAYS.map((d, i) => html`<button type="button" data-day="${i + 1}" aria-pressed="${String(days.has(i + 1))}" aria-label="${DAY_NAMES[i]}">${d}</button>`)}
        </div></div>` : html`<label class="field"><span>Date</span><input name="date" type="date" value="${first}" required></label>`}
      <div class="row-2">
        <label class="field"><span>Début</span><input name="start" type="time" value="${hhmm(ev.starts_at)}" required></label>
        <label class="field"><span>Fin</span><input name="end" type="time" value="${hhmm(ev.ends_at ?? null)}"></label>
      </div>
      ${withHall ? '' : html`<label class="field"><span>Lieu</span><input name="location" maxlength="120" value="${ev.location ?? ''}"></label>`}
      ${series ? html`<label class="field"><span>Jusqu'au</span><input name="until" type="date" value="${ev.recurrence!.until ?? ''}"></label>` : ''}
      ${withHall ? html`<label class="field"><span>Intitulé</span><input name="title" maxlength="120" value="${ev.title}"></label>` : ''}
      <label class="field"><span>Infos pour l'équipe</span><textarea name="note" rows="2" maxlength="500">${ev.note ?? ''}</textarea></label>
      ${series ? html`<p class="hint">S'applique à toute la série. Les séances déjà annulées le restent${ev.recurrence!.except?.length ? ` (${ev.recurrence!.except.length})` : ''}.</p>` : ''}
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Enregistrer</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-edit')!;
    const msg = $(f, '.form-msg')!;
    const startIn = $<HTMLInputElement>(f, '[name=start]')!, endIn = $<HTMLInputElement>(f, '[name=end]')!;
    const hall = withHall ? wireHallField($(f, '[data-hall-field]')!, {
      ctx: ctx!, clubId: () => team!.club_id, teamId: () => ev.team_id, eventId: ev.id, current: { hall_id: ev.hall_id ?? null, location: ev.location },
      when: () => ({ day: series ? first : $<HTMLInputElement>(f, '[name=date]')!.value, start: startIn.value, end: endIn.value,
        weekdays: series ? [...days] : null, until: series ? $<HTMLInputElement>(f, '[name=until]')!.value || null : null }),
      free: () => false,
      setTime: (d, s, e) => { const di = $<HTMLInputElement>(f, '[name=date]'); if (di) di.value = d; startIn.value = s; endIn.value = e; },
    }) : null;
    if (hall) f.addEventListener('change', () => hall.refresh());
    for (const b of $$<HTMLButtonElement>(f, '[data-day]')) b.addEventListener('click', () => {
      const d = Number(b.dataset.day);
      if (days.has(d)) days.delete(d); else days.add(d);
      b.setAttribute('aria-pressed', String(days.has(d)));
      hall?.refresh();
    });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const day = series ? first : String(d.get('date') ?? '');
      const start = String(d.get('start') ?? ''), end = String(d.get('end') ?? '');
      if (!day || !start) { msg.textContent = 'Indique la date et l\'heure.'; return; }
      const startsAt = new Date(`${day}T${start}`);
      const endsAt = end ? new Date(`${day}T${end}`) : null;
      if (endsAt && endsAt <= startsAt) { msg.textContent = 'La fin doit être après le début.'; return; }
      const until = String(d.get('until') ?? '');
      if (series && (!days.size || !until || until < day)) { msg.textContent = 'Choisis au moins un jour et une date de fin après la première séance.'; return; }
      try {
        await updateEvent(ev.id, {
          ...(ev.kind !== 'match' ? { title: String(d.get('title') ?? '').trim() || ev.title } : {}),
          starts_at: startsAt.toISOString(), ends_at: endsAt?.toISOString() ?? null,
          ...(hall ? hall.value() : { location: String(d.get('location') ?? '').trim() || null }), note: String(d.get('note') ?? '').trim() || null,
          ...(series ? { recurrence: { ...ev.recurrence, weekdays: [...days].sort(), until } } : {}),
        });
        close(); toast(series ? 'Série modifiée' : 'Modifié'); onDone();
      } catch { msg.textContent = 'Enregistrement impossible. Réessaie.'; }
    });
  });
}
